import type {
  AccountCommitment,
  AccountService,
  Hash,
  PoolAccount as SDKPoolAccount,
  PoolEventsError,
  RagequitEvent,
} from '@michael-leverup/privacy-pools-core-sdk';
import type { Hex } from 'viem';

export enum AccountEventType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  EXIT = 'exit',
  MIGRATION = 'migration',
}

export enum ReviewStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  DECLINED = 'declined',
  EXITED = 'exited',
  SPENT = 'spent',
  POI_REQUIRED = 'poi_required',
}

type RagequitEventWithTimestamp = RagequitEvent & {
  timestamp?: bigint;
};

export type PoolAccount = SDKPoolAccount & {
  name: number;
  balance: bigint;
  isValid: boolean;
  reviewStatus: ReviewStatus;
  lastCommitment: AccountCommitment;
  chainId: number;
  scope: Hash;
  isLegacy?: boolean;
  ragequit?: RagequitEventWithTimestamp;
};

export type HistoryData = {
  type: AccountEventType;
  amount: bigint;
  txHash: Hex;
  timestamp: number;
  reviewStatus: ReviewStatus;
  label: bigint;
  scope: Hash;
  chainId: number;
}[];

export type ReconstructionMetadataByChainScope = Record<
  string,
  {
    deposits: Array<{
      eventType: 'deposit';
      precommitmentHash: Hash;
      commitment: Hash;
      label: Hash;
      amount: bigint;
      address: string;
      txHash: Hex;
      logIndex: number;
      blockNumber: bigint;
      timestamp: number;
      reviewStatus: string;
    }>;
    withdrawals: Array<{
      eventType: 'withdrawal';
      spentNullifier: Hash;
      newCommitment: Hash;
      amount: bigint;
      txHash: Hex;
      logIndex: number;
      blockNumber: bigint;
      timestamp: number;
    }>;
    ragequits: Array<{
      eventType: 'ragequit';
      label: Hash;
      commitment: Hash;
      amount: bigint;
      address: string;
      txHash: Hex;
      logIndex: number;
      blockNumber: bigint;
      timestamp: number;
    }>;
  }
>;

export type LoadAccountResult = {
  accountService: AccountService;
  legacyAccountService: null;
  errors: PoolEventsError[];
  poolAccounts: PoolAccount[];
  poolAccountsByChainScope: Record<string, PoolAccount[]>;
  reconstructionMetadataByChainScope: ReconstructionMetadataByChainScope;
};
